import { useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { RemoveUserAccountApi } from "../../../../../../../../../services/Api/User/userTypes";
import { api } from "../../../../../../../../../services/Security/api";
import { useAuth } from "../../../../../../../../../services/Security/auth";
import { selectUser } from "../../../../../../../../../services/users/selectors";
import RemoveAccountConfirmationModal from "./RemoveAccountConfirmationModal";

const RemoveAccountForm = () => {
	const form = useForm<RemoveUserAccountApi>({
		criteriaMode: "all",
		defaultValues: {},
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		formState,
		reset,
		watch,
	} = form;
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const auth = useAuth();
	const user = useSelector(selectUser);
	const [showRemoveAccountConfirmationModal, setShowRemoveAccountConfirmationModal] = useState<boolean>(false);

	const onGenerateDeleteAccountToken = async () => {
		try {
			setShowRemoveAccountConfirmationModal(true);
			await api.user().generateRemoveAccountToken();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onSubmit = handleSubmit(async (data: any) => {
		setLoading(true);
		try {
			await api.user().removeUser(Number(user?.id), data);
			setShowRemoveAccountConfirmationModal(false);
			reset(data);
			addSuccessFlash(t("lib:common.flash.removed"));
			setLoading(false);
			await auth.logout();
		} catch (err) {
			handleError.form(err, setError, addFlash);
			setLoading(false);
		}
	});

	const getUniqueApplications = () => {
		return Array.from(
			new Set(
				user?.organizations
					?.map((x) => x.applications || [])
					.flat()
					.map((x) => x.name)
			)
		).join(", ");
	};

	return (
		<>
			<FormDirty formState={formState} key="profile-form" noValidate>
				<fieldset className={"form-group no-gutters"}>
					<FormInput
						label={t("modules.profile.field.reason.title")}
						placeholder={t("modules.profile.field.reason.placeholder")}
						help={`${t("modules.profile.field.reason.help_text")} ${user?.organizations
							?.map((x) => x.name)
							.join(", ")} ${t("modules.profile.field.reason.help_text2")} ${getUniqueApplications()}`}
						register={register}
						type="text"
						name="reason"
						errors={errors}
					/>
				</fieldset>
				<Button
					variant="danger"
					disabled={!watch("reason")}
					className="mb-2"
					onClick={onGenerateDeleteAccountToken}
				>
					{t("common.action.remove_account")}
				</Button>
			</FormDirty>
			{showRemoveAccountConfirmationModal && (
				<RemoveAccountConfirmationModal
					isShown={showRemoveAccountConfirmationModal}
					onHide={() => setShowRemoveAccountConfirmationModal(false)}
					form={form}
					loading={loading}
					handleRemove={onSubmit}
				/>
			)}
		</>
	);
};

export default RemoveAccountForm;
